import React, { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  ReferenceField,
  NumberField,
  SelectField,
  EditButton,
  ShowButton,
  DeleteButton,
  FilterForm,
  TextInput,
  SelectInput,
  DateInput,
  TopToolbar,
  ExportButton,
  BulkDeleteButton,
  useListContext,
  useNotify,
  useRefresh,
  useUpdate,
  FunctionField,
  WrapperField,
  useRecordContext,
  useCreatePath
} from 'react-admin';
import {
  Card,
  CardContent,
  Chip,
  Rating,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField as MuiTextField,
  Box,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Link as MuiLink
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Pending,
  MoreVert,
  Visibility,
  Edit,
  Delete,
  Flag,
  Star
} from '@mui/icons-material';
import { buildApiUrl } from '../config/api';
import { Link as RouterLink } from 'react-router-dom';

// Custom action buttons component
const ReviewActionButtons = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [adminDialog, setAdminDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await fetch(buildApiUrl(`reviews/admin/${record.id}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status: newStatus,
          adminNotes: adminNotes
        })
      });

      notify(`Review ${newStatus} successfully`, { type: 'success' });
      refresh();
    } catch (error) {
      notify('Error updating review status', { type: 'error' });
    }
    handleMenuClose();
    setAdminDialog(false);
    setAdminNotes('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle />;
      case 'rejected': return <Cancel />;
      case 'pending': return <Pending />;
      default: return <Pending />;
    }
  };

  return (
    <>
      <WrapperField>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={getStatusIcon(record.status)}
            label={record.status}
            color={getStatusColor(record.status)}
            size="small"
          />

          <IconButton onClick={handleMenuClick} size="small">
            <MoreVert />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {record.status !== 'approved' && (
              <MenuItem onClick={() => handleStatusChange('approved')}>
                <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                Aprobă
              </MenuItem>
            )}

            {record.status !== 'rejected' && (
              <MenuItem onClick={() => setAdminDialog(true)}>
                <Cancel sx={{ mr: 1, color: 'error.main' }} />
                Respinge
              </MenuItem>
            )}

            {record.status !== 'pending' && (
              <MenuItem onClick={() => handleStatusChange('pending')}>
                <Pending sx={{ mr: 1, color: 'warning.main' }} />
                Setează în așteptare
              </MenuItem>
            )}
          </Menu>
        </Box>
      </WrapperField>

      {/* Admin Notes Dialog */}
      <Dialog open={adminDialog} onClose={() => setAdminDialog(false)}>
        <DialogTitle>Respinge recenzia</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Opțional, specificați motivul respingerii:
          </Typography>
          <MuiTextField
            fullWidth
            multiline
            rows={3}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Motivul respingerii (opțional)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminDialog(false)}>Anulează</Button>
          <Button onClick={() => handleStatusChange('rejected')} color="error">
            Respinge recenzia
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Custom rating display component
const RatingDisplay = () => {
  const record = useRecordContext();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Rating value={record.rating} readOnly size="small" />
      <Typography variant="body2">({record.rating}/5)</Typography>
    </Box>
  );
};

// Custom review preview component
const ReviewPreview = () => {
  const record = useRecordContext();
  return (
    <Box sx={{ maxWidth: 300 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
        {record.title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}
      >
        {record.comment}
      </Typography>
    </Box>
  );
};

// Custom helpful votes display
const HelpfulVotesDisplay = () => {
  const record = useRecordContext();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Star sx={{ fontSize: 16, color: 'gold' }} />
      <Typography variant="body2">{record.helpfulVotes}</Typography>
    </Box>
  );
};

// Custom product display with link to product details
const ReviewProductField = () => {
  const record = useRecordContext();
  const createPath = useCreatePath();

  if (!record?.productId) {
    return <Typography variant="body2" color="text.secondary">Produs indisponibil</Typography>;
  }

  const productName = record.product?.name || record.productName || 'Produs';
  const productLink = createPath({ resource: 'products', id: record.productId, type: 'show' });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{productName}</Typography>
      <MuiLink
        component={RouterLink}
        to={productLink}
        underline="hover"
        sx={{ fontSize: '0.8rem', color: 'primary.main', fontWeight: 500 }}
      >
        Vezi produs
      </MuiLink>
    </Box>
  );
};

// Custom filters
const ReviewFilters = [
  <TextInput source="search" label="Caută" alwaysOn />,
  <SelectInput source="status" label="Status" choices={[
    { id: 'pending', name: 'În așteptare' },
    { id: 'approved', name: 'Aprobată' },
    { id: 'rejected', name: 'Respinsă' },
  ]} />,
  <SelectInput source="rating" label="Evaluare" choices={[
    { id: '5', name: '5 Stele' },
    { id: '4', name: '4 Stele' },
    { id: '3', name: '3 Stele' },
    { id: '2', name: '2 Stele' },
    { id: '1', name: '1 Stea' },
  ]} />,
];

// Custom actions toolbar
const ReviewListActions = () => {
  const { filterValues, setFilters } = useListContext();
  const hasFilters = Object.keys(filterValues).length > 0;
  const hasProductFilter = Boolean(filterValues.productId);

  const handleResetFilters = () => setFilters({}, []);

  const handleClearProductFilter = () => {
    const { productId, ...rest } = filterValues;
    setFilters(rest, []);
  };

  return (
    <TopToolbar sx={{ gap: 1 }}>
      <ExportButton />
      {hasProductFilter && (
        <Chip
          label="Filtru produs activ"
          onDelete={handleClearProductFilter}
          sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
        />
      )}
      {hasFilters && (
        <Button variant="outlined" size="small" onClick={handleResetFilters}>
          Șterge filtrele
        </Button>
      )}
    </TopToolbar>
  );
};

// Custom bulk actions
const ReviewBulkActions = () => (
  <BulkDeleteButton />
);

// Main ReviewList component
export const ReviewList = () => {
  return (
    <List
      title="Gestionare recenzii"
      filters={<FilterForm filters={ReviewFilters} />}
      actions={<ReviewListActions />}
      bulkActionButtons={<ReviewBulkActions />}
      perPage={25}
      sort={{ field: 'createdAt', order: 'DESC' }}
    >
      <Datagrid rowClick="show" bulkActionButtons={<ReviewBulkActions />}>
        <TextField source="userName" label="Client" />

        <FunctionField
          label="Produs"
          render={() => <ReviewProductField />}
        />

        <FunctionField
          label="Rating"
          render={() => <RatingDisplay />}
        />

        <FunctionField
          label="Recenzie"
          render={() => <ReviewPreview />}
        />

        <FunctionField
          label="Voturi utile"
          render={() => <HelpfulVotesDisplay />}
        />

        <DateField source="createdAt" label="Data" />

        <FunctionField
          label="Status"
          render={() => <ReviewActionButtons />}
        />

        <EditButton />
        <ShowButton />
        <DeleteButton />
      </Datagrid>
    </List>
  );
};

export default ReviewList;
